import { useRouterState } from "@tanstack/react-router";

export const LOCALES = ["en", "de", "pl", "es", "fr", "kz", "uz"] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "en";

/**
 * Launch gate — the single source of truth for public locales. Only these are
 * shown in the language switcher, indexable, and listed in hreflang/sitemap.
 * Partially rolled-out locales are additionally limited per path (./availability).
 */
export const LAUNCHED_LOCALES = ["en", "de", "pl"] as const satisfies readonly Locale[];
export type LaunchedLocale = (typeof LAUNCHED_LOCALES)[number];

export function isLaunchedLocale(locale: string): locale is LaunchedLocale {
  return (LAUNCHED_LOCALES as readonly string[]).includes(locale);
}

/** Locale codes that appear as a URL prefix (English stays un-prefixed). */
export const PREFIXED_LOCALES = LOCALES.filter((l) => l !== DEFAULT_LOCALE);

/**
 * `hreflang` value sent to crawlers for each locale. The two launched locales
 * are region-qualified (English targets Poland, German targets Germany); the
 * not-yet-launched locales stay plain language codes.
 */
export const HREFLANG: Record<Locale, string> = {
  en: "en-PL",
  de: "de-DE",
  pl: "pl-PL",
  es: "es",
  fr: "fr",
  kz: "kk",
  uz: "uz",
};

/** `<html lang>` value for each locale (mirrors the hreflang codes). */
export const HTML_LANG: Record<Locale, string> = { ...HREFLANG };

/** Open Graph locale codes use the `language_TERRITORY` form. */
export const OG_LOCALE: Record<Locale, string> = {
  en: "en_PL",
  de: "de_DE",
  pl: "pl_PL",
  es: "es_ES",
  fr: "fr_FR",
  kz: "kk_KZ",
  uz: "uz_UZ",
};


/**
 * BCP 47 tag used for user-facing date formatting. English keeps the
 * day-month-year style it always had; other locales use their HTML lang tag.
 */
export const DATE_LOCALE: Record<Locale, string> = {
  ...HTML_LANG,
  en: "en-GB",
  pl: "pl-PL",
};

export const SITE_URL = "https://catuik.com";

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

/** Reads the locale out of a pathname: `/de/brands` -> `de`, `/brands` -> `en`. */
export function localeFromPathname(pathname: string): Locale {
  const first = pathname.split("/").filter(Boolean)[0];
  return isLocale(first) ? first : DEFAULT_LOCALE;
}

/** Removes a locale prefix, always returning a path that starts with `/`. */
export function stripLocale(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  if (isLocale(segments[0])) segments.shift();
  return `/${segments.join("/")}`.replace(/\/$/, "") || "/";
}

/** Builds the URL of `path` (an un-prefixed, English path) in `locale`. */
export function localePath(path: string, locale: Locale): string {
  const clean = stripLocale(path.startsWith("/") ? path : `/${path}`);
  if (locale === DEFAULT_LOCALE) return clean;
  return clean === "/" ? `/${locale}` : `/${locale}${clean}`;
}

/** Current locale, derived from the URL so it survives SSR and deep links. */
export function useLocale(): Locale {
  return useRouterState({ select: (s) => localeFromPathname(s.location.pathname) });
}

/** Current path without its locale prefix — used to switch language in place. */
export function useBasePath(): string {
  return useRouterState({ select: (s) => stripLocale(s.location.pathname) });
}
